import { inject, Injectable, linkedSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { liveQuery } from 'dexie';
import { from } from 'rxjs';
import type { Device } from '../class/Device';
import ApiV1Adapter from '../database/api/ApiV1Adapter';
import { db } from '../database/db';
import EntityManager from '../database/EntityManager';
import { Landmark } from '../database/models';
import { AlarmsStateService } from './alarms-state.service';
import { DeviceStateService } from './device-state.service';

export const POLLING_DURATION = 60 * 1000; // fetch every minute.

export type CountedLandmark = Pick<Landmark, 'uid'> & { devices?: Landmark['devices'] };
type LocatedDevice = Pick<Device, 'uid' | 'poi' | 'uid_zoi'>;

/**
 * Fills `devices` of every landmark designated by a device's `poi` or `uid_zoi`, and returns those
 * landmarks in a new array, sorted by number of devices (most first).
 *
 * Landmarks active at the previous computation are reset first, so a device that left a landmark is
 * no longer counted in it. A device whose `poi` and `uid_zoi` designate the same landmark counts once.
 */
export function computeActiveLandmarks<T extends CountedLandmark>(
  landmarks: T[],
  devices: LocatedDevice[],
  previousActiveLandmarks: T[]
): T[] {
  previousActiveLandmarks.forEach(landmark => (landmark.devices = []));
  if (!landmarks.length || !devices.length) return [];

  const landmarksByUid = new Map(landmarks.map(landmark => [landmark.uid, landmark]));
  const activeLandmarks = new Set<T>();

  for (const device of devices) {
    for (const landmarkUid of new Set([device.poi, device.uid_zoi])) {
      const landmark = landmarkUid ? landmarksByUid.get(landmarkUid) : undefined;
      if (!landmark) continue;

      if (!activeLandmarks.has(landmark)) {
        landmark.devices = [];
        activeLandmarks.add(landmark);
      }
      landmark.devices.push(device.uid);
    }
  }

  return [...activeLandmarks].sort((first, second) => second.devices.length - first.devices.length);
}

@Injectable({
  providedIn: 'root',
})
class LandmarksService extends EntityManager<Landmark, 'poi'> {
  public defaultGetAll = () => this.table.orderBy('label').toArray(LandmarksService.withCategoriesIcon);
  public $toValidate = signal<Landmark[]>(null);
  public $landmarksById = signal<Record<number, Landmark> | null>(null);

  public values$ = from(
    liveQuery(async () => {
      const landmarks = await this.defaultGetAll();

      const landmarksById: Record<number, Landmark> = {};
      landmarks.forEach(landmark => {
        landmarksById[landmark.uid] = landmark;
      });
      this.$landmarksById.set(landmarksById);

      this.$toValidate.set(landmarks.filter(landmark => landmark.state === 'W'));

      return landmarks;
    })
  );
  public $values = toSignal(this.values$);

  private _deviceStateService = inject(DeviceStateService);
  private _alarmsStateService = inject(AlarmsStateService);

  // Not DevicesManagerService: it injects UnitsService, which injects this service (NG0200).
  public $activeLandmarks = linkedSignal({
    source: () => ({ landmarks: this.$values() ?? [], devices: this._deviceStateService.$devices() }),
    computation: ({ landmarks, devices }, previous?: { value: Landmark[] }) =>
      computeActiveLandmarks(landmarks, devices, previous?.value ?? []),
  });

  constructor() {
    super({
      name: 'poi',
      model: Landmark,
      polling: { duration: POLLING_DURATION, retrieveDelta: true },
      paging: landmarks => {
        if (landmarks.length >= 5000) {
          const lastLandmark = landmarks.at(-1);
          return { poi: lastLandmark.uid, timestamp: lastLandmark.timestamp };
        }
        return false;
      },
    });
  }

  static async withCategoriesIcon(landmarks: Landmark[]) {
    // Set only ids parent needed to improve the next bulkGet.
    const categoryIds = [...new Set(landmarks.map(landmark => landmark.uid_parent))];

    const categories = await db.poi_category.bulkGet(categoryIds);

    const categoryMap = new Map(categoryIds.map((id, index) => [id, categories[index]]));

    landmarks.forEach(landmark => {
      landmark.icon = categoryMap.get(landmark.uid_parent)?.symbol ?? 1;
    });

    return landmarks;
  }

  static async withCategoryIcon(landmark: Landmark) {
    const category = await db.poi_category.get(landmark.uid_parent);

    if (category) landmark.icon = category.symbol;

    return landmark;
  }

  public clear(): void {
    this.$landmarksById.set(null);
  }

  /**
   * Local optimistic patch of a poi's alarms list, without waiting for the
   * next poll — the poi's alarms field is authoritative from the alarm's own
   * params_alarm.uid_poi server-side, so this is UI-only and never persisted.
   */
  public setPoiAlarms(poiUid: number, alarms: number[]): void {
    const landmarksById = this.$landmarksById();
    const poi = landmarksById?.[poiUid];
    if (!poi) return;

    poi.alarms = alarms;
    this.$landmarksById.set({ ...landmarksById });
  }

  public async updateAll(landmarks: Landmark[]) {
    // /!\ Be careful, for now, this method its just use to update ui_parent of multiple landmarks.
    const api = new ApiV1Adapter('landmarks_update');
    const landmarksIds = landmarks.map(({ uid }) => uid);
    this.table.bulkPut(landmarks);

    await api.request({
      method: 'GET',
      body: { uid_landmarks: landmarksIds, uid_category: landmarks[0].uid_parent },
    });
  }

  public async deleteAll(ids: number[]) {
    const api = new ApiV1Adapter('landmarks_delete');

    this.table.bulkDelete(ids);

    await api.request({ method: 'GET', body: { uid_landmarks: ids } });
  }

  public getDevices(id: Landmark['uid']) {
    return this._deviceStateService.$devices().filter(device => device.poi === id || device.uid_zoi === id);
  }

  public getAlarms(id: Landmark['uid']) {
    return this._alarmsStateService.$alarms().filter(alarm => alarm?.params_alarm?.poi?.uid === id);
  }

  public watchLandmarkWithIcon(id: number) {
    return liveQuery(() => this.table.get(id, landmark => LandmarksService.withCategoryIcon(landmark)));
  }
}

export default LandmarksService;
